<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';

import PageLayout from 'dashboard/components-next/polaris/PageLayout.vue';
import SuggestedRules from 'dashboard/components-next/polaris/cards/SuggestedRules.vue';
import AddRuleField from 'dashboard/components-next/polaris/cards/AddRuleField.vue';
import ListToolbar from 'dashboard/components-next/polaris/cards/ListToolbar.vue';
import RuleCardSkin from 'dashboard/components-next/polaris/skin/RuleCardSkin.vue';
import BulkSelectBar from 'dashboard/components-next/polaris/assistant/BulkSelectBar.vue';
import PolarisEmptyState from 'dashboard/components-next/polaris/pageComponents/emptyStates/PolarisEmptyState.vue';
import {
  GUIDANCE_CATEGORIES,
  guidanceToCard,
  groupGuidanceCards,
  guidanceWithEdit,
} from 'dashboard/components-next/polaris/pageComponents/guidance/guidanceCategories';

// Guidelines (phase 4.4): the kit's plant, shared with Guardrails — the explanation, the kit's SuggestedRules, the
// kit's RuleCard (through RuleCardSkin: edit in place, Enter saves, Escape leaves), the ListToolbar and the kit's
// AddRuleField. The way back to Settings is the Topbar's breadcrumb (4.1). Same store calls and messages.
const { t, tm, rt } = useI18n();
const route = useRoute();
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();

const uiFlags = useMapGetter('polarisAssistants/getUIFlags');
const assistantId = computed(() => Number(route.params.assistantId));
const isFetching = computed(() => uiFlags.value.fetchingItem);
const assistant = computed(() =>
  store.getters['polarisAssistants/getRecord'](assistantId.value)
);

const searchQuery = ref('');

const guidelinesContent = computed(
  () => assistant.value?.response_guidelines || []
);

const displayGuidelines = computed(() =>
  guidelinesContent.value.map((c, idx) => guidanceToCard(c, idx))
);

// Fase 3: agrupado por categoria (strings legadas caem em communication_style sem título).
const categoryOptions = computed(() =>
  GUIDANCE_CATEGORIES.map(value => ({
    value,
    label: t(`POLARIS.GUIDANCE.CATEGORIES.${value.toUpperCase()}`),
  }))
);

// Suggested guidelines, own examples of tone and format (R2-4-9 = A; before, this page repeated the guidelines') (POLARIS.PAGES.GUIDELINE_EXAMPLES, en and pt_BR)
const guidelinesExample = computed(() =>
  tm('POLARIS.PAGES.GUIDELINE_EXAMPLES').map((content, index) => ({
    id: index + 1,
    content: rt(content),
  }))
);

const filteredGuidelines = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return displayGuidelines.value;
  return picoSearch(displayGuidelines.value, query, ['content']);
});

const groupedGuidelines = computed(() =>
  groupGuidanceCards(filteredGuidelines.value)
);

const shouldShowSuggestedRules = computed(() => {
  return uiSettings.value?.show_response_guidelines_suggestions !== false;
});

const closeSuggestedRules = () => {
  updateUISettings({ show_response_guidelines_suggestions: false });
};

// Bulk selection
const bulkSelectedIds = ref(new Set());

const handleRuleSelect = id => {
  const selected = new Set(bulkSelectedIds.value);
  selected[selected.has(id) ? 'delete' : 'add'](id);
  bulkSelectedIds.value = selected;
};

const buildSelectedCountLabel = computed(() => {
  const count = displayGuidelines.value.length || 0;
  const isAllSelected = bulkSelectedIds.value.size === count && count > 0;
  return isAllSelected
    ? t('POLARIS.ASSISTANTS.RESPONSE_GUIDELINES.BULK_ACTION.UNSELECT_ALL', {
        count,
      })
    : t('POLARIS.ASSISTANTS.RESPONSE_GUIDELINES.BULK_ACTION.SELECT_ALL', {
        count,
      });
});

const selectedCountLabel = computed(() => {
  return t('POLARIS.ASSISTANTS.RESPONSE_GUIDELINES.BULK_ACTION.SELECTED', {
    count: bulkSelectedIds.value.size,
  });
});

const saveGuidelines = async list => {
  await store.dispatch('polarisAssistants/update', {
    id: assistantId.value,
    assistant: { response_guidelines: list },
  });
};

const addGuideline = async (content, category = '') => {
  try {
    // Categoria fora do estilo de comunicação vira item {category, text}; o padrão segue string (legado).
    const item =
      category && category !== 'communication_style'
        ? { category, text: content }
        : content;
    const newGuidelines = [...guidelinesContent.value, item];
    await saveGuidelines(newGuidelines);
    useAlert(t('POLARIS.ASSISTANTS.RESPONSE_GUIDELINES.API.ADD.SUCCESS'));
  } catch (error) {
    useAlert(t('POLARIS.ASSISTANTS.RESPONSE_GUIDELINES.API.ADD.ERROR'));
  }
};

const editGuideline = async ({ id, content }) => {
  try {
    const updated = guidanceWithEdit(guidelinesContent.value, id, content);
    await saveGuidelines(updated);
    useAlert(t('POLARIS.ASSISTANTS.RESPONSE_GUIDELINES.API.UPDATE.SUCCESS'));
  } catch {
    useAlert(t('POLARIS.ASSISTANTS.RESPONSE_GUIDELINES.API.UPDATE.ERROR'));
  }
};

const deleteGuideline = async id => {
  try {
    const updated = guidelinesContent.value.filter((_, idx) => idx !== id);
    await saveGuidelines(updated);
    useAlert(t('POLARIS.ASSISTANTS.RESPONSE_GUIDELINES.API.DELETE.SUCCESS'));
  } catch {
    useAlert(t('POLARIS.ASSISTANTS.RESPONSE_GUIDELINES.API.DELETE.ERROR'));
  }
};

const bulkDeleteGuidelines = async () => {
  try {
    if (bulkSelectedIds.value.size === 0) return;
    const updated = guidelinesContent.value.filter(
      (_, idx) => !bulkSelectedIds.value.has(idx)
    );
    await saveGuidelines(updated);
    bulkSelectedIds.value.clear();
    useAlert(t('POLARIS.ASSISTANTS.RESPONSE_GUIDELINES.API.DELETE.SUCCESS'));
  } catch {
    useAlert(t('POLARIS.ASSISTANTS.RESPONSE_GUIDELINES.API.DELETE.ERROR'));
  }
};

const addAllExample = async () => {
  updateUISettings({ show_response_guidelines_suggestions: false });
  try {
    const exampleContents = guidelinesExample.value.map(
      example => example.content
    );
    const newGuidelines = [...guidelinesContent.value, ...exampleContents];
    await saveGuidelines(newGuidelines);
    useAlert(t('POLARIS.ASSISTANTS.RESPONSE_GUIDELINES.API.ADD.SUCCESS'));
  } catch {
    useAlert(t('POLARIS.ASSISTANTS.RESPONSE_GUIDELINES.API.ADD.ERROR'));
  }
};
</script>

<template>
  <PageLayout
    preview="open"
    :header-title="$t('POLARIS.ASSISTANTS.RESPONSE_GUIDELINES.TITLE')"
    :is-fetching="isFetching"
    :show-know-more="false"
    :show-pagination-footer="false"
  >
    <template #body>
      <p class="max-w-prose type-polaris-body text-polaris-muted-foreground">
        {{ $t('POLARIS.ASSISTANTS.RESPONSE_GUIDELINES.DESCRIPTION') }}
      </p>
      <SuggestedRules
        v-if="shouldShowSuggestedRules"
        :title="
          $t('POLARIS.ASSISTANTS.RESPONSE_GUIDELINES.ADD.SUGGESTED.TITLE')
        "
        :items="guidelinesExample"
        @add="item => addGuideline(item.content)"
        @add-all="addAllExample"
        @close="closeSuggestedRules"
      />
      <template v-if="displayGuidelines.length">
        <ListToolbar
          v-if="bulkSelectedIds.size === 0"
          v-model:search="searchQuery"
          :placeholder="
            t('POLARIS.ASSISTANTS.RESPONSE_GUIDELINES.LIST.SEARCH_PLACEHOLDER')
          "
          :count="displayGuidelines.length"
          count-key="POLARIS.PAGES.RULES_COUNT"
        />
        <BulkSelectBar
          v-else
          v-model="bulkSelectedIds"
          :all-items="displayGuidelines"
          :select-all-label="buildSelectedCountLabel"
          :selected-count-label="selectedCountLabel"
          :delete-label="
            $t(
              'POLARIS.ASSISTANTS.RESPONSE_GUIDELINES.BULK_ACTION.BULK_DELETE_BUTTON'
            )
          "
          @bulk-delete="bulkDeleteGuidelines"
        />
      </template>
      <PolarisEmptyState
        v-if="displayGuidelines.length === 0"
        :title="t('POLARIS.ASSISTANTS.RESPONSE_GUIDELINES.EMPTY_MESSAGE')"
      />
      <PolarisEmptyState
        v-else-if="filteredGuidelines.length === 0"
        :title="
          t('POLARIS.ASSISTANTS.RESPONSE_GUIDELINES.SEARCH_EMPTY_MESSAGE')
        "
      />
      <div v-else class="flex flex-col gap-5">
        <section
          v-for="group in groupedGuidelines"
          :key="group.category"
          class="flex flex-col gap-2"
        >
          <h2
            class="type-polaris-caption font-semibold uppercase tracking-wide text-polaris-muted-foreground"
          >
            {{
              t(`POLARIS.GUIDANCE.CATEGORIES.${group.category.toUpperCase()}`)
            }}
          </h2>
          <div v-for="guideline in group.items" :key="guideline.id">
            <p
              v-if="guideline.title"
              class="px-1 pb-0.5 type-polaris-caption text-polaris-muted-foreground"
            >
              {{ guideline.title }}
            </p>
            <RuleCardSkin
              :rule="guideline"
              selectable
              :selected="bulkSelectedIds.has(guideline.id)"
              @select="handleRuleSelect"
              @edit="editGuideline"
              @delete="deleteGuideline"
            />
          </div>
        </section>
      </div>
      <AddRuleField
        :placeholder="
          t('POLARIS.ASSISTANTS.RESPONSE_GUIDELINES.ADD.SUGGESTED.PLACEHOLDER')
        "
        :label="t('POLARIS.ASSISTANTS.RESPONSE_GUIDELINES.ADD.NEW.TITLE')"
        :categories="categoryOptions"
        @add="addGuideline"
      />
    </template>
  </PageLayout>
</template>
